import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, MapPin, Briefcase, Users } from 'lucide-react';
import { useGeolocation } from '@/hooks/useGeolocation';
import { cn } from '@/lib/utils';

interface JobLeadSelectorProps {
  selectedJob: string | null;
  selectedLead: string | null;
  onSelectJob: (id: string) => void;
  onSelectLead: (id: string) => void;
}

export function JobLeadSelector({
  selectedJob,
  selectedLead,
  onSelectJob,
  onSelectLead,
}: JobLeadSelectorProps) {
  const { location, calculateDistance } = useGeolocation();

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user.id)
        .single();
      
      return data;
    }
  });

  const { data: jobs, isLoading: jobsLoading } = useQuery({
    queryKey: ['jobs', profile?.org_id],
    queryFn: async () => {
      if (!profile?.org_id) return [];
      
      const { data } = await supabase
        .from('jobs')
        .select('*')
        .eq('org_id', profile.org_id)
        .order('created_at', { ascending: false })
        .limit(20);
      
      return data || [];
    },
    enabled: !!profile?.org_id,
  });

  const { data: leads, isLoading: leadsLoading } = useQuery({
    queryKey: ['leads', profile?.org_id],
    queryFn: async () => {
      if (!profile?.org_id) return [];
      
      const { data } = await supabase
        .from('leads')
        .select('*')
        .eq('org_id', profile.org_id)
        .in('status', ['qualified', 'new', 'proposal_sent'])
        .order('created_at', { ascending: false })
        .limit(20);
      
      return data || [];
    },
    enabled: !!profile?.org_id,
  });

  // Sort by proximity if location available
  const sortedJobs = jobs ? [...jobs].sort((a, b) => {
    if (!location) return 0;
    
    // For now, just return original order (can add lat/lng to jobs table later)
    return 0;
  }) : [];

  const sortedLeads = leads ? [...leads].sort((a, b) => {
    if (!location) return 0;
    
    // For now, just return original order (can add lat/lng to leads table later)
    return 0;
  }) : [];

  if (jobsLoading || leadsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Jobs */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Briefcase className="w-5 h-5" />
          <h2 className="font-semibold">Active Jobs</h2>
          {location && (
            <Badge variant="outline" className="ml-auto">
              <MapPin className="w-3 h-3 mr-1" />
              Sorted by distance
            </Badge>
          )}
        </div>
        <div className="space-y-2">
          {sortedJobs.map((job) => (
            <Card
              key={job.id}
              className={cn(
                "p-4 cursor-pointer transition-all hover:border-primary",
                selectedJob === job.id && "border-primary bg-primary/5"
              )}
              onClick={() => onSelectJob(job.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium">{job.property_address}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {job.owner_name || 'No owner'}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="secondary">{job.status}</Badge>
                    {job.job_number && (
                      <span className="text-xs text-muted-foreground">
                        #{job.job_number}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Leads */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-5 h-5" />
          <h2 className="font-semibold">Hot Leads</h2>
        </div>
        <div className="space-y-2">
          {sortedLeads.map((lead) => (
            <Card
              key={lead.id}
              className={cn(
                "p-4 cursor-pointer transition-all hover:border-primary",
                selectedLead === lead.id && "border-primary bg-primary/5"
              )}
              onClick={() => onSelectLead(lead.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium">{lead.contact_name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {lead.property_address || 'No address'}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="secondary">{lead.status}</Badge>
                    {lead.source && (
                      <span className="text-xs text-muted-foreground">
                        {lead.source}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
